import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonIconGhostMedium from '../common/ButtonIconGhost/ButtonIconGhostMedium';
import ButtonSecondaryMedium from '../common/ButtonSecondary/ButtonSecondaryMedium';
import SwitchComponent from '../common/SwitchComponent/SwitchComponent';
import { Icon } from '../icon/icon';
import { DEFAULT_OFFSET_M } from '../../three/core/dimension/dimensionTypes';
import { DIM_UNITS } from '../../utils/dimension/dimensionFormat';
import { cn } from '../../utils/string/cn.utils';
import { Field, GROUP_LABELS, Segmented } from './DimensionControls';
import {
  closeDimensionEditor,
  closeDimensionMenu,
  duplicateDimension,
  openDimensionEditor,
  removeChain,
  removeDimension,
  setDimensionUnit,
  updateDimension,
} from '../../store/reducers/dimension.reducer';

const AXIS_LABELS = {
  width: 'Width',
  height: 'Height',
  depth: 'Depth',
  custom: 'Aligned',
};

// ---------------------------------------------------------------------------
// Thành phần giao diện nhỏ dùng trong panel
// ---------------------------------------------------------------------------

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-12 p-16 border-0 border-t border-solid border-outline-default-divider first:border-t-0">
    <span className="text-label-small-emphasized uppercase tracking-wide text-black-medium">
      {title}
    </span>
    {children}
  </div>
);

const formatNumber = (value, precision) =>
  String(Number(Number(value ?? 0).toFixed(precision)));

/**
 * Ô nhập số có đơn vị. Giữ text cục bộ khi đang gõ (không bị "nhảy" giá trị),
 * chấp nhận cả dấu phẩy, kẹp min/max và chuẩn hoá khi rời ô.
 */
const NumberField = ({
  value,
  onCommit,
  min = -Infinity,
  max = Infinity,
  step = 1,
  precision = 2,
  suffix,
  ariaLabel,
}) => {
  const [text, setText] = useState(formatNumber(value, precision));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(formatNumber(value, precision));
  }, [value, focused, precision]);

  const parse = raw => {
    const n = Number(String(raw).trim().replace(',', '.'));
    return String(raw).trim() !== '' && Number.isFinite(n) ? n : null;
  };
  const clamp = n => Math.max(min, Math.min(max, n));

  return (
    <div className="flex items-center h-[32px] w-[104px] rounded-[6px] bg-fills-white border border-solid border-outline-default-primary focus-within:border-outline-brand-primary overflow-hidden">
      <input
        aria-label={ariaLabel}
        inputMode="decimal"
        className="w-full h-full px-[8px] border-0 outline-none bg-trans text-body-medium text-right"
        value={text}
        onFocus={e => {
          setFocused(true);
          e.target.select();
        }}
        onChange={e => {
          setText(e.target.value);
          const n = parse(e.target.value);
          if (n !== null) onCommit(clamp(n));
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const dir = e.key === 'ArrowUp' ? 1 : -1;
            const next = clamp((parse(text) ?? value) + dir * step);
            setText(formatNumber(next, precision));
            onCommit(next);
          }
          // Không để phím tắt của designer (xoá module, xoay...) bắt phím gõ
          e.stopPropagation();
        }}
      />
      {suffix && (
        <span className="px-[8px] text-body-small text-black-medium bg-fills-primary-varient h-full flex items-center border-0 border-l border-solid border-outline-default-primary">
          {suffix}
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Menu chuột phải
// ---------------------------------------------------------------------------

const MenuItem = ({ children, onClick, danger, checked }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center justify-between w-full h-[32px] px-[12px] border-0 bg-trans cursor-pointer text-body-medium text-left hover:bg-primary-primaryFill',
      danger ? 'text-Accents-Red' : 'text-black-high'
    )}
  >
    <span>{children}</span>
    {checked && (
      <span className="w-[16px] h-[16px] text-primary-primary">
        <Icon.ICCheck />
      </span>
    )}
  </button>
);

const MenuDivider = () => (
  <div className="h-[1px] my-[4px] bg-outline-default-divider" />
);

/** Menu chuột phải trên một DIM: sửa, ẩn/hiện, nhân bản, chuyển nhóm, xoá. */
const DimensionContextMenu = ({ menu, dim }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    const close = () => dispatch(closeDimensionMenu());
    const onKey = e => e.key === 'Escape' && close();
    // Đóng khi bấm ra ngoài; menu tự chặn pointerdown của chính nó
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [dispatch]);

  const run = action => () => {
    dispatch(action);
    dispatch(closeDimensionMenu());
  };

  // Giữ menu trong khung nhìn
  const left = Math.min(menu.x, window.innerWidth - 216);
  const top = Math.min(menu.y, window.innerHeight - 330);

  return (
    <div
      className="fixed w-[208px] py-[4px] bg-fills-white rounded-[8px] shadow-dropshadow8"
      style={{ left: Math.max(8, left), top: Math.max(8, top), zIndex: 40 }}
      onPointerDown={e => e.stopPropagation()}
      onContextMenu={e => e.preventDefault()}
    >
      <MenuItem onClick={run(openDimensionEditor(dim.id))}>
        {t('Edit properties')}
      </MenuItem>
      <MenuItem
        onClick={run(
          updateDimension({ id: dim.id, changes: { visible: !dim.visible } })
        )}
      >
        {dim.visible ? t('Hide DIM') : t('Show DIM')}
      </MenuItem>
      <MenuItem onClick={run(duplicateDimension(dim.id))}>
        {t('Duplicate')}
      </MenuItem>
      <MenuDivider />
      <div className="px-[12px] py-[2px] text-label-small text-black-medium">
        {t('Move to group')}
      </div>
      {Object.entries(GROUP_LABELS).map(([group, label]) => (
        <MenuItem
          key={group}
          checked={dim.group === group}
          onClick={run(updateDimension({ id: dim.id, changes: { group } }))}
        >
          {t(label)}
        </MenuItem>
      ))}
      <MenuDivider />
      <MenuItem danger onClick={run(removeDimension(dim.id))}>
        {t('Delete')}
      </MenuItem>
      {dim.chainId && (
        <MenuItem danger onClick={run(removeChain(dim.chainId))}>
          {t('Delete chain')}
        </MenuItem>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bảng thuộc tính
// ---------------------------------------------------------------------------

/** Bảng thuộc tính: chỉ đổi cách hiển thị, không đổi kích thước thật. */
const DimensionProperties = ({ dim, unit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const update = changes => dispatch(updateDimension({ id: dim.id, changes }));
  const updateStyle = style => update({ style });
  // Ghi chú kỹ thuật: không phải số đo nên bỏ các tuỳ chọn của đường kích thước
  const isNote = dim.type === 'annotation';

  return (
    <div
      className="fixed w-[280px] flex flex-col bg-[#F7F7F7] rounded-[8px] shadow-dropshadow8 overflow-hidden"
      style={{
        right: 16,
        top: 72,
        maxHeight: 'calc(100vh - 96px)',
        zIndex: 30,
      }}
      // Phím gõ trong panel không được kích hoạt phím tắt của designer
      onKeyDown={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-8 p-16 bg-fills-white border-0 border-b border-solid border-outline-default-divider">
        <div className="flex flex-col gap-4 min-w-0">
          <span className="text-title-large text-nowrap">
            {isNote ? t('Note properties') : t('DIM properties')}
          </span>
          <div className="flex items-center gap-4">
            <span className="px-[8px] py-[2px] rounded-[100px] bg-primary-primaryFill text-primary-primary text-label-small-emphasized">
              {isNote ? t('Note') : t(AXIS_LABELS[dim.axis] ?? 'Custom')}
            </span>
            <span className="px-[8px] py-[2px] rounded-[100px] bg-fills-secondary text-black-medium text-label-small">
              {isNote
                ? t('Technical note')
                : dim.autoFor
                  ? t('Auto DIM')
                  : t('Manual DIM')}
            </span>
          </div>
        </div>
        <ButtonIconGhostMedium
          icon={<Icon.ICClose />}
          aria-label={t('Close')}
          onClick={() => dispatch(closeDimensionEditor())}
        />
      </div>

      <div className="overflow-y-auto">
        <div className="mx-16 mt-12 p-[10px] rounded-[8px] bg-primary-primaryFill text-body-small text-black-high">
          {t(isNote ? 'DIM_NOTE_INTRO' : 'DIM_PANEL_INTRO')}
        </div>

        {dim.chainId && (
          <div className="mx-16 mt-8 p-[10px] rounded-[8px] bg-fills-secondary text-body-small text-black-high">
            {t('DIM_CHAIN_NOTE')}
          </div>
        )}

        {dim.orphan && (
          <div className="mx-16 mt-8 p-[10px] rounded-[8px] bg-Accents-orange-fill text-body-small text-Accents-orange">
            {t(
              isNote
                ? 'DIM_NOTE_ORPHAN'
                : 'The module this DIM measures no longer exists'
            )}
          </div>
        )}

        {isNote && (
          <Section title={t('Content')}>
            <textarea
              aria-label={t('Text')}
              autoFocus
              rows={3}
              value={dim.text ?? ''}
              placeholder={t('Type the note here')}
              onChange={e => update({ text: e.target.value })}
              className="w-full p-[8px] rounded-[6px] bg-fills-white border border-solid border-outline-default-primary text-body-medium outline-none focus:border-outline-brand-primary resize-y"
            />
          </Section>
        )}

        <Section title={t('Appearance')}>
          <Field label={t('Color')} hint={t('DIM_HINT_COLOR')}>
            <label className="flex items-center gap-8 cursor-pointer">
              <span className="text-body-small text-black-medium uppercase">
                {dim.style.color}
              </span>
              <input
                type="color"
                aria-label={t('Color')}
                value={dim.style.color}
                onChange={e => updateStyle({ color: e.target.value })}
                className="w-[32px] h-[32px] p-0 border-0 bg-trans cursor-pointer rounded-[6px]"
              />
            </label>
          </Field>
          <Field label={t('Font size')} hint={t('DIM_HINT_FONT_SIZE')}>
            <NumberField
              ariaLabel={t('Font size')}
              value={dim.style.fontSize}
              min={8}
              max={32}
              suffix="px"
              onCommit={fontSize => updateStyle({ fontSize })}
            />
          </Field>
          <Field label={t('Line width')} hint={t('DIM_HINT_LINE_WIDTH')}>
            <NumberField
              ariaLabel={t('Line width')}
              value={dim.style.lineWidth}
              min={0.5}
              max={4}
              step={0.25}
              suffix="px"
              onCommit={lineWidth => updateStyle({ lineWidth })}
            />
          </Field>
          {!isNote && (
            <>
              <Field label={t('Decimals')} hint={t('DIM_HINT_DECIMALS')}>
                <Segmented
                  value={dim.style.decimals}
                  onChange={decimals => updateStyle({ decimals })}
                  options={[0, 1, 2, 3].map(n => ({
                    value: n,
                    label: String(n),
                  }))}
                />
              </Field>
              <Field label={t('Arrow style')} hint={t('DIM_HINT_ARROW')}>
                <Segmented
                  value={dim.style.arrowType}
                  onChange={arrowType => updateStyle({ arrowType })}
                  options={[
                    { value: 'arrow', label: t('Arrow') },
                    { value: 'tick', label: t('Tick') },
                  ]}
                />
              </Field>
            </>
          )}
        </Section>

        <Section title={t('Position')}>
          {!isNote && (
            <Field
              label={t('Distance from object')}
              hint={t('DIM_HINT_OFFSET')}
            >
              <NumberField
                ariaLabel={t('Distance from object')}
                value={(dim.offsetM ?? 0) * 1000}
                min={-10000}
                max={10000}
                step={10}
                precision={0}
                suffix="mm"
                onCommit={mm => update({ offsetM: mm / 1000 })}
              />
            </Field>
          )}
          <Field
            label={isNote ? t('Box position') : t('Number position')}
            hint={t('DIM_HINT_RESET')}
          >
            <ButtonSecondaryMedium
              className="whitespace-nowrap"
              title={t('Reset position')}
              onClick={() =>
                update(
                  isNote
                    ? { labelShift: [60, -50] }
                    : { offsetM: DEFAULT_OFFSET_M, labelShift: [0, 0] }
                )
              }
            />
          </Field>
        </Section>

        <Section title={t('Display')}>
          {!isNote && (
            <Field label={t('Unit')} hint={t('DIM_HINT_UNIT')}>
              <Segmented
                value={unit}
                onChange={u => dispatch(setDimensionUnit(u))}
                options={DIM_UNITS.map(u => ({ value: u, label: u }))}
              />
            </Field>
          )}
          <Field label={t('Group')} hint={t('DIM_HINT_GROUP')}>
            <select
              aria-label={t('Group')}
              className="h-[32px] w-[132px] px-[8px] rounded-[6px] bg-fills-white border border-solid border-outline-default-primary text-body-medium outline-none focus:border-outline-brand-primary"
              value={dim.group}
              onChange={e => update({ group: e.target.value })}
            >
              {Object.entries(GROUP_LABELS).map(([group, label]) => (
                <option key={group} value={group}>
                  {t(label)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Visible')} hint={t('DIM_HINT_VISIBLE')}>
            <SwitchComponent
              size="default"
              id={`dim-visible-${dim.id}`}
              value={dim.visible}
              onChange={visible => update({ visible })}
            />
          </Field>
        </Section>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-8 p-16 bg-fills-white border-0 border-t border-solid border-outline-default-divider">
        <ButtonSecondaryMedium
          title={t('Duplicate')}
          onClick={() => dispatch(duplicateDimension(dim.id))}
        />
        <button
          type="button"
          onClick={() => dispatch(removeDimension(dim.id))}
          className="h-[32px] px-[12px] rounded-[6px] bg-fills-white border border-solid border-Accents-Red text-Accents-Red text-body-medium font-semibold cursor-pointer hover:bg-Accents-Red-Fill-Varient"
        >
          {t('Delete')}
        </button>
      </div>
    </div>
  );
};

const DimensionEditor = () => {
  const { enabled, items, editingId, contextMenu, unit } = useSelector(
    state => state.dimension
  );
  if (!enabled) return null;

  const editing = items.find(item => item.id === editingId);
  const menuDim = contextMenu && items.find(item => item.id === contextMenu.id);

  return (
    <>
      {editing && <DimensionProperties dim={editing} unit={unit} />}
      {menuDim && <DimensionContextMenu menu={contextMenu} dim={menuDim} />}
    </>
  );
};

export default DimensionEditor;
